import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useScopeParams } from "@/hooks/useScopeParams";
import { PortfolioMetricTile } from "@/views/Portfolio/shared";
import InfoTooltip from "@/components/UI/InfoTooltip";
import {
  fetchTrialDocuments,
  groupSections,
  needsAttention,
  MetricsCard,
  NeedsAttentionTable,
  StagedChecklist,
  EXPIRY_WINDOW_DAYS,
} from "@/components/Documents/tmf";

/**
 * Site-profile document view, structured for its two audiences:
 * - Site staff work the ACTION QUEUE — overdue filings, expirations inside the
 *   renewal window, and open monitor queries are the things a coordinator must
 *   act on today to stay current.
 * - Sponsors/CRAs read the SECTION CHECKLIST — collected-vs-expected per
 *   artifact section is how inspection readiness is judged at close-out.
 *
 * Takes no site id: ISF sources attribute documents to the workspace operating
 * the trial, not to individual sites, so both views cover the whole trial
 * workspace corpus and the scope comes from the route.
 */
export default function DocumentsTab() {
  const { id: trialId } = useScopeParams();

  const { data, isLoading } = useQuery({
    queryKey: ["site-documents", trialId],
    queryFn: () => fetchTrialDocuments(trialId!),
    enabled: !!trialId,
    staleTime: 60_000,
  });

  const rows = useMemo(() => data?.results ?? [], [data]);
  const now = Date.now();

  const sections = useMemo(() => groupSections(rows, now), [rows, now]);
  const attention = useMemo(() => needsAttention(rows, now), [rows, now]);

  const kpis = useMemo(() => {
    const expected = data?.count ?? rows.length;
    const collected = rows.filter((r) => r.document_type === "content").length;
    return {
      expected,
      collected,
      completeness: expected
        ? `${((100 * collected) / expected).toFixed(1)}%`
        : "—",
      openQueries: rows.reduce((n, r) => n + (r.open_reviews ?? 0), 0),
      overdue: rows.filter(
        (r) =>
          r.document_type === "placeholder" &&
          r.due_date &&
          Date.parse(r.due_date) < now,
      ).length,
    };
  }, [rows, data, now]);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        <span className="text-[14px] font-semibold text-[#1F2933]">
          TMF / ISF documents
        </span>
        <InfoTooltip title="Documents from the ISF workspace operating this site's trial. The action queue lists what site staff must resolve now; the section checklist shows sponsors and CRAs collected-vs-expected completeness per artifact section — the basis of inspection readiness at close-out. ISF sources do not attribute documents to individual sites." />
      </div>

      <MetricsCard columns={5}>
        <PortfolioMetricTile label="Expected" value={kpis.expected} />
        <PortfolioMetricTile label="Collected" value={kpis.collected} />
        <PortfolioMetricTile label="Completeness" value={kpis.completeness} />
        <PortfolioMetricTile
          label="Open monitor queries"
          value={kpis.openQueries}
        />
        <PortfolioMetricTile label="Overdue filings" value={kpis.overdue} />
      </MetricsCard>

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold text-[#1F2933]">
            Action queue
          </span>
          <InfoTooltip
            title={
              `For site staff: everything that needs action now — overdue ` +
              `filings, documents expiring within ${EXPIRY_WINDOW_DAYS} days, ` +
              `and monitor queries awaiting a response.`
            }
          />
        </div>
        <NeedsAttentionTable rows={attention} />
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold text-[#1F2933]">
            Section checklist
          </span>
          <InfoTooltip title="For sponsors and CRAs: inspection readiness by artifact section, organized by trial stage — the activation package collected before the site opened, conduct documents accumulating now, and close-out artifacts at the end. Completed stages remain on file and fully visible. Expand a section to see its documents. ISF sources attribute documents to the trial workspace, not to individual sites." />
        </div>
        <StagedChecklist sections={sections} loading={isLoading} />
      </div>
    </div>
  );
}
